import { PreferenceCategory } from '@atlassian/browser-storage-controls';

import { PRFilterState } from 'src/sections/repository/sections/pull-request-list/types';
import { DEFAULT_PR_FILTER_PREFERENCES } from 'src/sections/workspace/sections/pullrequests/constants';
import {
  isPRUserFilterType,
  PRListType,
  PRUserFilterType,
} from 'src/sections/workspace/sections/pullrequests/types';
import { store, isStorageCategoryAllowed } from 'src/utils/atlassian-store';

const PR_STATES = [
  'OPEN',
  'MERGED',
  'DECLINED',
  'SUPERSEDED',
  'DRAFT',
  'QUEUED',
  'ALL',
];

// Local storage key for PR filter preferences
const PR_FILTER_PREFERENCES_KEY = 'pr-list-filter-preferences';

// Valid PRListType values for validation
const VALID_PR_LIST_TYPES = new Set<PRListType>([
  PRListType.Repository,
  PRListType.Workspace,
]);

export function buildStatesBBQL(states: PRFilterState[]): string {
  // Return early if the states array is empty or contains 'ALL'.
  // The API will return all PRs with an empty query string.
  if (states.length === 0 || states.includes('ALL')) {
    return '';
  }

  let showDraft: boolean = false;
  let showQueued: boolean = false;

  const statesSet = new Set<string>();
  for (const state of states) {
    if (PR_STATES.includes(state)) {
      if (state === 'DRAFT') {
        showDraft = true;
      } else if (state === 'QUEUED') {
        showQueued = true;
      } else {
        statesSet.add(`"${state}"`);
      }
    } else {
      // Return early if the state is invalid
      return '';
    }
  }

  let stateQuery = '';
  if (statesSet.size > 1) {
    // Multiple states -> 'state IN ("state1","state2", ...)'
    stateQuery = `state IN (${Array.from(statesSet).join(',')})`;
  } else if (statesSet.size === 1) {
    // Single state -> 'state="state1"'
    stateQuery = `state=${statesSet.values().next().value}`;
  }

  let draftQuery = '';
  let queuedQuery = '';
  let wrapOutput = false;

  // Special case: if both DRAFT and QUEUED are selected and OPEN is not selected
  if (showDraft && showQueued && !statesSet.has('"OPEN"')) {
    // Combine both conditions under a single state="OPEN" clause
    const combinedQuery = 'state="OPEN" AND (draft=true OR queued=true)';

    if (stateQuery.length > 0) {
      // If there are other states, we need to OR them with the combined query
      return `(${stateQuery} OR (${combinedQuery}))`;
    } else {
      // No other states, just return the combined query
      return combinedQuery;
    }
  }

  // Context: state="OPEN" will return all open and draft PRs
  // If we intend to show both open and draft PRs, we don't need to add anything to the draftQuery.
  if (showDraft && !statesSet.has('"OPEN"')) {
    // This branch means we want to show draft PRs, but not open PRs.

    // Other states like "MERGED" and "DECLINED" have a value of draft=false.
    // So, we can't just append ' AND draft=true' to the stateQuery because it will exclude
    // those states.
    draftQuery =
      stateQuery.length > 0
        ? ' OR (state="OPEN" AND draft=true)'
        : 'state="OPEN" AND draft=true';
    // Whenever we add the "OR" operator, we need to wrap the entire query in
    // parentheses to preserve the correct precedence.
    wrapOutput = stateQuery.length > 0;
  } else if (!showDraft && statesSet.has('"OPEN"')) {
    // This branch means we want to show open PRs, but not draft PRs.
    draftQuery = ' AND draft=false';
  }

  if (showQueued && !statesSet.has('"OPEN"')) {
    // This branch means we want to show queued PRs, but not open PRs.
    queuedQuery =
      stateQuery.length > 0 || draftQuery.length > 0
        ? ' OR (state="OPEN" AND queued=true)'
        : 'state="OPEN" AND queued=true';
    // Wrap output if we're adding an OR operator
    wrapOutput = stateQuery.length > 0 || draftQuery.length > 0;
  } else if (!showQueued && statesSet.has('"OPEN"')) {
    // This branch means we want to show open PRs, but not queued PRs.
    queuedQuery = ' AND queued=false';
  }

  if (wrapOutput) {
    return `(${stateQuery}${draftQuery}${queuedQuery})`;
  }

  return `${stateQuery}${draftQuery}${queuedQuery}`;
}

function validatePrState(storedPrState: any): PRFilterState[] | undefined {
  if (storedPrState == null || !Array.isArray(storedPrState)) {
    return undefined;
  }

  if (storedPrState.length === 0) {
    // If the stored prStates are an empty array, return 'ALL' as the default
    // value since a filter of 'ALL' will not actually apply any filter to the BBQL
    // query to fetch the PR list.
    return ['ALL'] as PRFilterState[];
  }

  const prStates = storedPrState.filter(
    (state: any): state is PRFilterState =>
      typeof state === 'string' && PR_STATES.includes(state)
  );

  return prStates.length > 0 ? prStates : undefined;
}

function validateUserFilter(
  storedUserFilter: any
): PRUserFilterType | undefined {
  if (
    storedUserFilter == null ||
    typeof storedUserFilter !== 'string' ||
    !isPRUserFilterType(storedUserFilter)
  ) {
    return undefined;
  }
  return storedUserFilter;
}

export function getPRFilterPreferences(prListType: PRListType): {
  prState: PRFilterState[];
  userFilter: PRUserFilterType;
} {
  if (!isStorageCategoryAllowed(PreferenceCategory.Functional)) {
    return DEFAULT_PR_FILTER_PREFERENCES[prListType];
  }

  try {
    const preferences = store.get(PR_FILTER_PREFERENCES_KEY);

    // Return defaults if we don't have any preferences stored
    if (preferences == null) {
      return DEFAULT_PR_FILTER_PREFERENCES[prListType];
    }

    // Ensure preferences are a valid object syntactically
    if (typeof preferences !== 'object' || Array.isArray(preferences)) {
      return DEFAULT_PR_FILTER_PREFERENCES[prListType];
    }

    const listPreferences = preferences[prListType];

    // Ensure that preferences for the list type are a valid object syntactically
    if (
      listPreferences == null ||
      typeof listPreferences !== 'object' ||
      Array.isArray(listPreferences)
    ) {
      return DEFAULT_PR_FILTER_PREFERENCES[prListType];
    }

    const { prState: storedPrState, userFilter: storedUserFilter } =
      listPreferences;

    const { prState: defaultPrState, userFilter: defaultUserFilter } =
      DEFAULT_PR_FILTER_PREFERENCES[prListType];

    // Validate and use stored values, falling back to defaults
    const prState = validatePrState(storedPrState) ?? defaultPrState;
    const userFilter =
      validateUserFilter(storedUserFilter) ?? defaultUserFilter;

    return {
      prState,
      userFilter,
    };
  } catch {
    return DEFAULT_PR_FILTER_PREFERENCES[prListType];
  }
}

export async function setPRFilterPreferences(
  prListType: PRListType,
  options: {
    prState?: PRFilterState[];
    userFilter?: PRUserFilterType;
  }
): Promise<void> {
  const { prState, userFilter } = options;

  try {
    // Get existing preferences to merge with new values
    let existingPreferences = store.get(PR_FILTER_PREFERENCES_KEY);

    // Validate outer preferences structure
    if (
      existingPreferences == null ||
      typeof existingPreferences !== 'object' ||
      Array.isArray(existingPreferences)
    ) {
      existingPreferences = {};
    }

    // Remove any keys that aren't valid PRListType values
    const cleanedPreferences: Record<string, any> = {};
    for (const [key, value] of Object.entries(existingPreferences)) {
      if (VALID_PR_LIST_TYPES.has(key as PRListType)) {
        cleanedPreferences[key] = value;
      }
    }

    // Get existing preferences for the specified prListType
    const existingPreferencesListType = cleanedPreferences[prListType];

    // Build updated preferences
    const updatedListPreferences: {
      prState?: PRFilterState[];
      userFilter?: PRUserFilterType;
    } = {};

    // Use provided prState, or validate and use stored value
    if (prState !== undefined) {
      updatedListPreferences.prState = prState;
    } else {
      const validatedStoredPrState = validatePrState(
        existingPreferencesListType?.prState
      );
      if (validatedStoredPrState !== undefined) {
        updatedListPreferences.prState = validatedStoredPrState;
      }
    }

    // Use provided userFilter, or validate and use stored value
    if (userFilter !== undefined) {
      updatedListPreferences.userFilter = userFilter;
    } else {
      const validatedStoredUserFilter = validateUserFilter(
        existingPreferencesListType?.userFilter
      );
      if (validatedStoredUserFilter !== undefined) {
        updatedListPreferences.userFilter = validatedStoredUserFilter;
      }
    }

    // Update the full preferences object
    const updatedPreferences = {
      ...cleanedPreferences,
      [prListType]: updatedListPreferences,
    };

    await store.set(PR_FILTER_PREFERENCES_KEY, updatedPreferences);
  } catch {
    // Silently fail if we can't set local storage values
  }
}
